import {
	AssetSchema,
	getAssetTypePrefixBySystemType,
} from '@innovyze/lib_asset_schema';
import { AssetTypeId, SystemTypeId } from '@Types/assetSchema.types';
import { camelCase, upperFirst } from 'lodash-es';

type GetAssetTypeIdParams =
	| {
			systemType: string;
			assetType: string;
	  }
	| { assetSchema: AssetSchema };

/**
 * Normalises a stored system type to its asset-schema id by dropping the spaces
 * in the display name: 'Sanitary Sewer' -> 'SanitarySewer'. All six resolve from
 * either spelling; an id passed in comes back unchanged.
 *
 * Not `upperFirst(camelCase(...))`, which lowercases runs of capitals and would
 * turn 'ESRIGas' into 'EsriGas'. The typeof guard is because these values come
 * off an API response inside the network try/catch in Rehab.sagas, where a
 * TypeError would surface as a tree list failing with "No additional
 * information".
 */
const correctSystemType = (systemType?: string): string =>
	typeof systemType === 'string' ? systemType.replace(/\s+/g, '') : '';

/**
 * Normalises a stored asset type to its asset-schema id by reattaching the system
 * type's prefix: ('pipe', 'Sanitary Sewer') -> 'wwPipe', ('pipe', 'Stormwater')
 * -> 'swPipe'. Already-prefixed values pass through, as does anything under a
 * system the library has no prefix for - today Water Distribution and both ESRI
 * systems, and a newly Rehab-enabled one would join them silently.
 *
 * It rewrites rather than validates, so unrecognised input is mangled rather than
 * left alone: a UUID asset type under Sanitary Sewer becomes 'ww12C46Cfd...'.
 * Nothing persists the result (see getNewAssetIds); a schema-validity check is
 * tracked separately.
 *
 * The `upperFirst(camelCase(...))` here is what correctSystemType deliberately
 * avoids, and is safe only because no ww/sw/cb id has a run of capitals - a
 * future 'wwACPipe' would break. Likewise the startsWith short-circuit is safe
 * only because no prefix-stripped id starts with its own prefix: a future
 * 'swSwale' would pass through unprefixed. The sweep in the test file is what
 * catches both, so don't delete it. See there too for which stored spellings
 * recover and which don't.
 */
const correctAssetType = (assetType?: string, systemType?: string): string => {
	const trimmed = typeof assetType === 'string' ? assetType.trim() : '';
	if (!trimmed) {
		return trimmed;
	}

	const prefix = getAssetTypePrefixBySystemType(
		correctSystemType(systemType),
	);

	return prefix && !trimmed.startsWith(prefix)
		? `${prefix}${upperFirst(camelCase(trimmed))}`
		: trimmed;
};

/**
 * Resolves the asset-schema ids for a system/asset type, from an asset schema or
 * from the plain strings the rehab API returns.
 *
 * I360A-2657: one identity, the schema id. No per-combo special-casing, and the
 * legacy `externalId` is gone - which relies on saas-am-rehab normalising both
 * parameters on every consumer. Verified against DEV, not verifiable from here.
 *
 * Normalisation on read is permanent, not transitional. Pre-migration rows hold a
 * display name ('Sanitary Sewer') or an unprefixed asset type ('pipe') and nothing
 * rewrites them: creation stores schema ids, but saveRehabTreeSaga omits both
 * fields, so an update sends neither. Every read normalises, for the row's life.
 *
 * The id reaches assetSchemaSelectorByType, the asset and risk APIs, the map
 * deep-link and the unit lookup - grep for `assetType.id` rather than trusting a
 * list here, which drifted twice during I360A-2514 before being removed.
 */
export function getNewAssetIds(params: GetAssetTypeIdParams): {
	systemTypeId: SystemTypeId;
	assetTypeId: AssetTypeId;
} {
	// A freshly picked type carries canonical ids, so there is nothing to
	// normalise.
	if ('assetSchema' in params) {
		return {
			systemTypeId: { id: params.assetSchema?.systemType?.id ?? '' },
			assetTypeId: { id: params.assetSchema?.id ?? '' },
		};
	}

	const { systemType, assetType } = params;

	return {
		systemTypeId: { id: correctSystemType(systemType) },
		assetTypeId: { id: correctAssetType(assetType, systemType) },
	};
}
